import React, { useEffect, useState } from 'react';
import ModalPortal from '../../../components/common/ModalPortal';

interface ReconnectionIndicatorProps {
  visible: boolean;
}

/**
 * ReconnectionIndicator - Shows a clear visual indicator during reconnection
 * 
 * This component provides user feedback during reconnection to let them
 * know the interview is being re-established.
 */
const ReconnectionIndicator: React.FC<ReconnectionIndicatorProps> = ({ visible }) => {
  const [dots, setDots] = useState('.');
  
  useEffect(() => {
    if (!visible) return;
    
    // Animate the dots for a loading effect
    const interval = setInterval(() => {
      setDots(prev => {
        if (prev.length >= 3) return '.';
        return prev + '.';
      });
    }, 500);
    
    return () => clearInterval(interval);
  }, [visible]);
  
  if (!visible) return null;
  
  return (
    <ModalPortal>
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in z-[100]">
        <div className="bg-surface-light dark:bg-surface-dark rounded-xl shadow-xl max-w-md w-full border border-border-light dark:border-border-dark animate-fade-in-up">
          <div className="p-5 border-b border-border-light dark:border-border-dark">
            <div className="flex items-center">
              <h3 className="text-lg font-semibold text-text-primary-light dark:text-text-primary-dark">
                Connecting
              </h3>
            </div>
          </div>
          <div className="p-5">
            <div className="flex items-center justify-center mb-4">
              {/* Pulsing animation for immediate visual feedback */}
              <div className="relative w-12 h-12">
                <div className="absolute inset-0 bg-primary-light/30 dark:bg-primary-dark/30 rounded-full animate-ping"></div>
                <div className="absolute inset-2 bg-primary-light dark:bg-primary-dark rounded-full flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 text-white">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.288 15.038a5.25 5.25 0 0 1 7.424 0M5.106 11.856c3.807-3.808 9.98-3.808 13.788 0M1.924 8.674c5.565-5.565 14.587-5.565 20.152 0M12.53 18.22l-.53.53-.53-.53a.75.75 0 0 1 1.06 0Z" />
                  </svg>
                </div>
              </div>
            </div>
            
            <p className="text-base text-text-secondary-light dark:text-text-secondary-dark text-center">
              <span className="text-text-primary-light dark:text-text-primary-dark">Connecting{dots}</span><br />
              <span className="text-sm mt-2 block">
                Please wait while we establish your interview connection.
              </span>
            </p>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
};

export default ReconnectionIndicator; 